// First-touch lead attribution. Field names match src/php/contact.php.
const STORAGE_KEY = 'tt_attribution';
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const FUTURE_SKEW_MS = 60 * 1000;

const UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term'
];

const ATTRIBUTION_FIELDS = UTM_FIELDS.concat([
  'landing_page',
  'referrer_url',
  'referrer_domain',
  'first_touch_at'
]);

const LIMITS = {
  utm_source: 150,
  utm_medium: 150,
  utm_campaign: 150,
  utm_content: 150,
  utm_term: 150,
  landing_page: 500,
  referrer_url: 500,
  referrer_domain: 253,
  first_touch_at: 40
};

const UTM_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 ._+/:@|-]{0,149}$/;
const PATH_PATTERN = /^\/[A-Za-z0-9._~/-]*$/;
const LANDING_PATTERN = /^\/[A-Za-z0-9._~/-]*(\?[A-Za-z0-9_=&%+.-]{1,400})?$/;
const REFERRER_URL_PATTERN = /^https?:\/\/[A-Za-z0-9.-]+(?::[0-9]{1,5})?(?:\/[A-Za-z0-9._~/-]*)?$/;
const DOMAIN_PATTERN = /^(?=.{1,253}$)(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)*[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?$/;
const TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;

function patternFor(name) {
  if (name === 'landing_page') return LANDING_PATTERN;
  if (name === 'referrer_url') return REFERRER_URL_PATTERN;
  if (name === 'referrer_domain') return DOMAIN_PATTERN;
  if (name === 'first_touch_at') return TIMESTAMP_PATTERN;
  return UTM_PATTERN;
}

function isAccepted(name, value) {
  if (typeof value !== 'string' || value.length > LIMITS[name]) return false;
  if (value === '') return name !== 'first_touch_at' && name !== 'landing_page';
  return patternFor(name).test(value);
}

function stripControls(raw) {
  return Array.from(String(raw)).filter((char) => {
    const code = char.charCodeAt(0);
    return code > 31 && code !== 127;
  }).join('');
}

function cleanUtm(raw) {
  const value = stripControls(raw).trim();
  if (value.length > LIMITS.utm_source || !UTM_PATTERN.test(value)) return '';
  return value;
}

function safePath(pathname) {
  const path = String(pathname || '/').startsWith('/') ? String(pathname || '/') : `/${pathname}`;
  if (path.length <= LIMITS.landing_page && PATH_PATTERN.test(path)) return path;
  return '/';
}

function externalReferrer() {
  const empty = { url: '', domain: '' };
  if (!document.referrer) return empty;

  let url;
  try {
    url = new URL(document.referrer);
  } catch {
    return empty;
  }

  if (url.origin === window.location.origin) return empty;
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return empty;

  const referrerUrl = `${url.origin}${url.pathname}`;
  const urlOk = referrerUrl.length <= LIMITS.referrer_url
    && REFERRER_URL_PATTERN.test(referrerUrl);
  const domainOk = url.hostname.length <= LIMITS.referrer_domain
    && DOMAIN_PATTERN.test(url.hostname);

  return {
    url: urlOk ? referrerUrl : '',
    domain: domainOk ? url.hostname : ''
  };
}

function captureCurrent() {
  const params = new URLSearchParams(window.location.search);
  const kept = new URLSearchParams();
  const record = {
    landing_page: '/',
    referrer_url: '',
    referrer_domain: '',
    first_touch_at: new Date().toISOString()
  };

  UTM_FIELDS.forEach((name) => {
    const value = cleanUtm(params.get(name) || '');
    record[name] = value;
    if (value) kept.set(name, value);
  });

  const path = safePath(window.location.pathname);
  const query = kept.toString();
  const landing = query ? `${path}?${query}` : path;
  const landingOk = landing.length <= LIMITS.landing_page && LANDING_PATTERN.test(landing);
  record.landing_page = landingOk ? landing : path;

  const referrer = externalReferrer();
  record.referrer_url = referrer.url;
  record.referrer_domain = referrer.domain;
  return record;
}

function isFresh(record) {
  if (!record || typeof record !== 'object') return false;

  const namesOk = ATTRIBUTION_FIELDS.every((name) => isAccepted(name, record[name]));
  if (!namesOk) return false;

  const touched = Date.parse(record.first_touch_at);
  if (Number.isNaN(touched)) return false;

  const age = Date.now() - touched;
  return age <= MAX_AGE_MS && age >= -FUTURE_SKEW_MS;
}

function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isFresh(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStored(record) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

function fillFields(record) {
  ATTRIBUTION_FIELDS.forEach((name) => {
    const value = isAccepted(name, record[name]) ? record[name] : '';
    const fields = document.querySelectorAll(`input[type="hidden"][name="${name}"]`);
    Array.from(fields).forEach((field) => {
      const input = field;
      input.value = value;
    });
  });
}

function clearFields() {
  const empty = {};
  ATTRIBUTION_FIELDS.forEach((name) => {
    empty[name] = '';
  });
  fillFields(empty);
}

function deleteStored() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

// Called only with the Analytics consent choice. A denied choice removes any
// stored record without reading it. A granted choice keeps the first touch.
function applyAttribution(analyticsGranted) {
  if (!analyticsGranted) {
    deleteStored();
    clearFields();
    return;
  }

  const stored = readStored();
  const record = stored || captureCurrent();
  if (!stored) writeStored(record);
  fillFields(record);
}

export default applyAttribution;
